import * as React from 'react';
import { Alert, Button, Input, Modal, Panel, Select } from '@ixicare/ui';
import {
  Beacon,
  Device,
  DeviceEntryProps,
  DeviceType,
  ECoordsSrid,
  Gateway,
  TUpdateCoords,
  WatcherrApiError,
  deviceTypes,
} from '@ixicare/resources';
import { t } from '~i18n';
import { OnMarkerInsertEvent } from '~components/Map/DynamicMap';
import { useMutation } from '@tanstack/react-query';
import { humaniseErrors } from '~utils/error';
import { Spacer } from '~components/Utils/Spacer';
import { capitalize } from 'lodash';

export const buttonOptions = [
  {
    label: t('device.types.regular_button'),
    value: 'REGULAR_BUTTON',
  },
  {
    label: t('devices.table.emergencyButton'),
    value: 'EMERGENCY_BUTTON',
  },
];

export function CreateInstalledDeviceModal({ onClose, onRefresh, newLocation, siteId }) {
  const [code, setCode] = React.useState<string>('');
  const { typeNumber, macAddress } = React.useMemo(() => getTypeAndMac(code), [code]);
  const type = typeof typeNumber === 'string' ? deviceTypes[typeNumber] : undefined;

  const typeTranslation = React.useMemo(() => type && t(`device.types.${type.toLowerCase()}`), [type]);
  const [description, setDescription] = React.useState('');
  const [isTouched, setIsTouched] = React.useState(false);

  React.useEffect(() => {
    if (!isTouched) {
      setDescription(
        newLocation?.zoneName ? `${newLocation.zoneName} ${capitalize(typeTranslation?.toLowerCase() ?? '-')}` : ''
      );
    }
  }, [typeTranslation, newLocation]);

  const [emergencyButton, setEmergencyButton] = React.useState<DeviceEntryProps['subType']>('REGULAR_BUTTON');

  const createMutation = useMutation({
    mutationFn: () => createDevice(type, macAddress ?? '', description, newLocation, siteId, emergencyButton),
    onSuccess: () => {
      onRefresh();
      onClose();
    },
  });

  return (
    <Modal style={{ maxWidth: 450 }}>
      <>
        <h3 className="text-center mb-30">{t('device.form.create', { type: typeTranslation })}</h3>
        {createMutation.error && <Alert type="error">{humaniseErrors(createMutation.error as Error).title}</Alert>}
        <Input
          label={t('device.form.code')}
          autoCapitalize="characters"
          value={code}
          onValueChange={setCode}
          error={getFieldError('deviceType', createMutation.error) || getFieldError('macAddress', createMutation.error)}
        />
        <Spacer size={16} />
        {type == 'WALL_MOUNTED_DISTRESS_BUTTON' && (
          <>
            <Panel.Subtitle>{t('device.form.subtype')}</Panel.Subtitle>

            <Select
              value={t(`${emergencyButton}`)}
              onChange={(value) => {
                setEmergencyButton(value);
              }}
              options={buttonOptions}
            />

            <Spacer size={32} />
          </>
        )}
        <Input
          label={t('device.form.description')}
          value={description}
          onValueChange={(x) => {
            setDescription(x);
            setIsTouched(true);
          }}
          error={getFieldError('description', createMutation.error)}
        />
        <Spacer size={32} />
        <Button
          className="mb-15 error"
          disabled={createMutation.isPending || !description.length || !typeNumber || !macAddress}
          loading={createMutation.isPending}
          isBlock
          buttonType="success"
          type="button"
          onClick={() => createMutation.mutate()}
        >
          {t('device.form.confirm')}
        </Button>
        <Button onClick={() => onClose()} disabled={createMutation.isPending} isBlock buttonType="link">
          {t('global.cancel')}
        </Button>
      </>
    </Modal>
  );
}

const getFieldError = (key: string, error?: any) => {
  const fieldErrors = error instanceof WatcherrApiError ? error.body?.fieldErrors[key] : undefined;
  return fieldErrors ? fieldErrors.join(', ') : undefined;
};

const getTypeAndMac = (data?: string) => {
  const split = (data ?? '').split(' ');
  if (split.length !== 2) return { typeNumber: undefined, macAddress: undefined };

  return {
    typeNumber: split[0].length === 1 ? `0${split[0]}` : split[0],
    macAddress: split[1],
  };
};

const createDevice = async (
  type: DeviceType,
  macAddress: string,
  description: string,
  location: OnMarkerInsertEvent,
  siteId: number,
  emergencyButton?: string | null
) => {
  const installationCoords: TUpdateCoords = {
    x: location.x,
    y: location.y,
    srid: ECoordsSrid.Indoor,
  };

  if (type === DeviceType.BEACON) {
    return Beacon.submitBeacon({
      macAddress,
      distanceToLeftWall: 0,
      distanceToRightWall: 0,
      description,
      zoneId: location.zoneUuid,
      planeId: location.planeId,
      installationCoords,
    });
  }
  if (type === DeviceType.GATEWAY) {
    return Gateway.submitGateway({
      macAddress,
      description,
      zoneId: location.zoneUuid!,
      installationCoords,
      planeId: location.planeId,
    });
  }
  if (emergencyButton == 'EMERGENCY_BUTTON') {
    return Device.submitDevice({
      siteId: siteId,
      deviceType: type,
      macAddress,
      description,
      planeId: location.planeId,
      installationCoords,
      subType: emergencyButton,
    });
  } else {
    return Device.submitDevice({
      siteId: siteId,
      deviceType: type,
      macAddress,
      description,
      planeId: location.planeId,
      installationCoords,
    });
  }
};
